// The Products, Add a product, product settings and Page layout screens' rules, worked out from the
// rows they read and the app's clock. Pure: no network and no React, so the spec checks it on its own.
import { uk, time, shortDate, longDate, duration, daysFrom, clock } from '../../lib/format.js'
import { staffFirstName } from '../../lib/names.js'
import { LIVE_COHORT_HOME, WEEK_PAGE, MASTERMIND_HOME, POYT_HOME, HOME_SECTION_WORDS, layoutKind } from '../../product/layout.js'
import { ON_DEMAND_HOME } from '../../products/ondemand/layout.js'
import { prefixFor } from '../../product/screens.js'
import { TYPES, PIECES, ADD } from './copy.js'

// ---- Types and looks ---------------------------------------------------------------------------

export const TYPE_LABEL = Object.fromEntries(TYPES.map(t => [t.key, t.name]))
export const typeLabel = type => TYPE_LABEL[type] || type

// Which way a type sells: always paid, always free, or either (catalogue_type_fixes_access)
export function accessFor(type) {
  if (type === 'webinar' || type === 'linked') return 'free'
  if (type === 'resource_pack') return 'either'
  return 'paid'
}

// Add a product makes every type (Adam, 13 Oct: "I would expect to create anything that I want to be
// able to create"). A webinar isn't a product: its tile opens Recordings and dates, Webinars, where
// each 1 is added. Duplicate shows on every product that has moved in, except the 1 that holds every
// webinar (Free webinars), which there's only ever 1 of.
export const canDuplicate = p => Boolean(p) && isMovedIn(p) && p.type !== 'webinar'

// The looks a type's pages are drawn in (decision 1 of the product-types plan, 13 Oct): a mastermind
// and an on-demand course in the Accelerator family, as the Accelerator and Property Cashflow For Life
// are; a masterclass in the Profit On Your Terms family, as the video is; a private client space in
// the master brand, as the 1-2-1 Mentorship is. A live cohort and a resource pack take any look.
export const LOOKS_FOR = { mastermind: ['accelerator'], on_demand: ['accelerator'], masterclass: ['poyt'], client_space: ['master'] }
export const looksFor = type => LOOKS_FOR[type] || LOOK_ORDER

// A product made in Add a product or by Duplicate (newProductRow and duplicateRow set created_by;
// config.sql and the seed never do, so every product that came with a move has none). Only these get
// the switch that makes their Stripe purchases live, Add a module and their modules in Recordings.
export const madeInAdmin = p => Boolean(p?.created_by)

// Each look's colours, as tokens (styles/tokens.css), and its heading font, for tiles and previews.
// An own look carries its own colours, which are data, not brand values.
export const LOOKS = {
  master: { bg: 'var(--shell-bg)', accent: 'var(--shell-gold)', text: 'var(--shell-ink)', font: "'Manrope', sans-serif", weight: 800 },
  accelerator: { bg: 'var(--acc-bg)', accent: 'var(--acc-green)', text: 'var(--acc-green)', font: "'Inter Tight', sans-serif", weight: 700 },
  funding_engine: { bg: 'var(--fe-bg)', accent: 'var(--fe-green)', text: 'var(--fe-text)', font: "'Anton', sans-serif", weight: 400 },
  poyt: { bg: 'var(--poyt-navy)', accent: 'var(--poyt-orange)', text: 'var(--on-dark)', font: "'Archivo', sans-serif", weight: 800 },
}
export const LOOK_ORDER = ['master', 'accelerator', 'funding_engine', 'poyt', 'own']

export const FONTS = [
  { key: 'Manrope', name: 'Manrope', family: "'Manrope', sans-serif", weight: 800 },
  { key: 'Inter Tight', name: 'Inter Tight', family: "'Inter Tight', sans-serif", weight: 700 },
  { key: 'Archivo', name: 'Archivo', family: "'Archivo', sans-serif", weight: 800 },
  { key: 'Anton', name: 'ANTON', family: "'Anton', sans-serif", weight: 400 },
  { key: 'IBM Plex Sans', name: 'IBM Plex Sans', family: "'IBM Plex Sans', sans-serif", weight: 700 },
]
export const fontFor = key => FONTS.find(f => f.key === key) || FONTS[0]

// The tile colours for a look, own looks from their own_look
export function lookColours(look, own) {
  if (look === 'own') {
    const f = fontFor(own?.heading_font)
    return { bg: own?.bg || '#FFFFFF', accent: own?.accent || '#0D0D0D', text: own?.text || '#0D0D0D', font: f.family, weight: f.weight }
  }
  return LOOKS[look] || LOOKS.master
}

// A product's cover on the Products board: its look, and the board's variants (the masterclass's
// orange, Property Cashflow For Life's green, The Funding Engine's capitals)
export function coverStyle(p) {
  const name = p.name
  if (p.access_kind === 'free') {
    const card = p.settings?.card || {}
    if (p.type === 'webinar') return { cls: 'ink', text: name }
    if (card.tone === 'solid') return { cls: 'green', text: card.art_headline || name }
    const text = name.length > 32 ? name.split(' ').slice(0, 2).join(' ') : name
    if (p.look === 'accelerator') return { cls: 'acc', text }
    return { cls: 'light', text }
  }
  if (p.look === 'own') return { cls: 'own', text: name, own: lookColours('own', p.own_look) }
  if (p.look === 'funding_engine') return { cls: 'fe', text: name.toUpperCase() }
  if (p.look === 'poyt') return { cls: p.type === 'masterclass' ? 'orange' : 'navy', text: name }
  if (p.look === 'accelerator') return { cls: p.type === 'on_demand' ? 'green' : 'acc', text: name }
  return { cls: 'master', text: name }
}

// ---- Contrast (WCAG 2), as the board's own check does it -------------------------------------------

const HEX = /^#?[0-9a-fA-F]{6}$/
export const isHex = s => HEX.test(String(s || '').trim())
function lum(hex) {
  const h = String(hex).trim().replace('#', '')
  const c = [0, 2, 4].map(i => parseInt(h.substr(i, 2), 16) / 255).map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
}
export function ratio(a, b) {
  if (!isHex(a) || !isHex(b)) return null
  const x = lum(a), y = lum(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}
// 4.48 shows as 4.4, so a number that shows as passing always passes
export const ratioText = r => (Math.floor(r * 10) / 10).toFixed(1)

// The 3 lines of Add a product's contrast check, and whether all 3 pass
export function contrastCheck({ bg, accent, text }) {
  const textRatio = ratio(bg, text)
  const onWhite = ratio(accent, '#FFFFFF'), onDark = ratio(accent, '#0D0D0D')
  const btnInk = onWhite !== null && onDark !== null && onWhite >= onDark ? 'white' : 'near-black'
  const btnRatio = onWhite === null || onDark === null ? null : Math.max(onWhite, onDark)
  const edge = ratio(accent, bg)
  const rows = [
    { key: 'text', ratio: textRatio, need: 4.5 },
    { key: 'button', ratio: btnRatio, need: 4.5, ink: btnInk },
    { key: 'edge', ratio: edge, need: 3 },
  ].map(r => ({ ...r, pass: r.ratio !== null && r.ratio >= r.need }))
  return { rows, pass: rows.every(r => r.pass), btnInk }
}

export const normalHex = s => {
  const h = String(s || '').trim().replace('#', '')
  return HEX.test(h) ? `#${h.toUpperCase()}` : String(s || '').trim()
}

// ---- Addresses ----------------------------------------------------------------------------------

// The board's web address: the name, a leading "the" dropped, lower case, words joined by -
export function slugFor(name) {
  const s = String(name || '').trim().toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return s.slice(0, 60).replace(/-+$/, '') || ''
}

// The addresses the app itself answers (src/app/App.jsx: the Account, Free resources, Free webinars and
// admin pages, a certificate's check, the login link's page) and the 7 products' own paths, so no new
// product's address is ever 1 of them: a product called "Admin" gets admin-2
export const RESERVED_SLUGS = [
  'account', 'admin', 'free-resources', 'free-webinars', 'verify', 'sign-in', 'tools',
  'masterclass', 'funding-engine', 'poyt', 'cashflow', 'accelerator', 'mentorship',
]

// A slug no product or app page has yet: the slug, then -2, -3 ...
export function uniqueSlug(base, taken) {
  const used = new Set([...RESERVED_SLUGS, ...taken])
  const root = base || 'product'
  if (!used.has(root)) return root
  for (let n = 2; n < 1000; n++) {
    const s = `${root}-${n}`
    if (!used.has(s)) return s
  }
  return `${root}-${Date.now()}`
}

// Old addresses (AdminProducts.dc.html), each with its real state. A forward is a change outside the
// hub at each product's move (stage 9's poyt. zip, then the others), so in phase 1 none is live yet;
// flip live with the code change that makes the move.
export const MOVES = [
  { slug: 'poyt', from: 'course.adamslee.co.uk', to: 'learn.adamslee.co.uk/poyt. The Week 6 slides name the old address.', live: false, when: 'Once Profit On Your Terms moves in, after its cohort 1.' },
  { slug: 'cashflow', from: 'cashflow.adamslee.co.uk', to: 'learn.adamslee.co.uk/cashflow', live: false, when: 'At its launch in the new year.' },
  { slug: 'masterclass', from: 'poyt.adamslee.co.uk/watch/...', to: 'learn.adamslee.co.uk/masterclass', live: false, when: 'When the masterclass moves in.' },
  { slug: 'funding-engine', from: 'The Funding Engine Notion pages', to: 'Kept for cohort 3, then each page points to the hub', live: false, when: 'When The Funding Engine moves in, after cohort 3.' },
  { slug: 'accelerator', from: 'members.adamslee.co.uk', to: 'learn.adamslee.co.uk, once the Accelerator moves in', live: false, when: 'In phase 2, when the Accelerator moves in.' },
]

// Where a product that hasn't moved in lives until it does (CLAUDE.md, "Products that haven't moved in yet")
const OUTSIDE_HOSTS = { poyt: 'course.adamslee.co.uk', accelerator: 'members.adamslee.co.uk' }
export const outsideHost = p => OUTSIDE_HOSTS[p?.slug] || 'its own site'
export const isMovedIn = p => p?.moved_in !== false

// ---- The "now" line under each product's name --------------------------------------------------

const startOfUkDay = (nowMs, dayOffset = 0) => {
  const u = uk(nowMs)
  return u.day + dayOffset
}

// "tonight at 7pm", "today at 2pm", "tomorrow at 7pm", "on Wed 7 Oct at 7pm"
export function whenWords(t, nowMs) {
  const d = daysFrom(nowMs, t)
  if (d === 0) return `${uk(t).hh >= 17 ? 'tonight' : 'today'} at ${time(t)}`
  if (d === 1) return `tomorrow at ${time(t)}`
  return `on ${shortDate(t)} at ${time(t)}`
}

const cohortRuns = (c, nowMs) => {
  if (!c.starts_on) return false
  const today = startOfUkDay(nowMs)
  const start = uk(`${c.starts_on}T12:00:00Z`).day
  if (start > today) return false
  if (c.ends_on) return uk(`${c.ends_on}T12:00:00Z`).day >= today
  if (c.weeks_total) return start + c.weeks_total * 7 > today
  return true
}
const cohortAhead = (c, nowMs) => c.starts_on && uk(`${c.starts_on}T12:00:00Z`).day > startOfUkDay(nowMs)

// The board's lines: 'Cohort 3 running, Week 4 tonight at 7pm. Next cohort: no dates yet.',
// 'Cohort 1 starts Wed 21 Oct, 7pm. Start here is open.', 'Founding group 1 and Group 2. Next
// sessions Sun 18 Oct and Sun 25 Oct, 5pm.', '1 hour 48 minutes. The Profit On Your Terms offer
// appears at 1:35:00.', 'In build. Moves in from cashflow.adamslee.co.uk.', '1 client: Laura Grant,
// since Thu 3 Sep. Next session Thu 1 Oct, 7pm.'
export function nowLine(p, d, nowMs) {
  if (!isMovedIn(p)) return `On ${outsideHost(p)} until it moves.`
  const cohorts = (d.cohorts || []).filter(c => c.product_id === p.id)
  const sessions = (d.sessions || []).filter(s => s.product_id === p.id && s.status !== 'cancelled' && s.starts_at && Date.parse(s.starts_at) + (s.duration_minutes || 90) * 60e3 > nowMs)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))
  switch (p.type) {
    case 'live_cohort': {
      const running = cohorts.filter(c => cohortRuns(c, nowMs)).sort((a, b) => String(b.starts_on).localeCompare(String(a.starts_on)))[0]
      const ahead = cohorts.filter(c => cohortAhead(c, nowMs)).sort((a, b) => String(a.starts_on).localeCompare(String(b.starts_on)))[0]
      const startHere = (d.steps || []).some(s => s.product_id === p.id && s.section === 'start_here' && s.is_published)
      const parts = []
      if (running) {
        const next = sessions.find(s => s.cohort_id === running.id)
        parts.push(next ? `${running.name} running, ${next.title} ${whenWords(next.starts_at, nowMs)}.` : `${running.name} running.`)
        parts.push(ahead ? `Next cohort: ${ahead.name} starts ${shortDate(`${ahead.starts_on}T12:00:00Z`)}.` : 'Next cohort: no dates yet.')
      } else if (ahead) {
        const first = sessions.find(s => s.cohort_id === ahead.id)
        parts.push(`${ahead.name} starts ${first ? `${shortDate(first.starts_at)}, ${time(first.starts_at)}` : shortDate(`${ahead.starts_on}T12:00:00Z`)}.`)
        if (startHere) parts.push('Start here is open.')
      } else {
        parts.push('No intake running. Next cohort: no dates yet.')
      }
      return parts.join(' ')
    }
    case 'mastermind': {
      const groups = cohorts.filter(c => c.kind === 'group').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map(c => c.name)
      const next = sessions.slice(0, 2)
      const names = groups.length ? `${joinAnd(groups)}.` : 'No groups yet.'
      if (!next.length) return `${names} No sessions booked.`
      const sameTime = next.every(s => time(s.starts_at) === time(next[0].starts_at))
      const dates = sameTime
        ? `${joinAnd(next.map(s => shortDate(s.starts_at)))}, ${time(next[0].starts_at)}`
        : joinAnd(next.map(s => `${shortDate(s.starts_at)}, ${time(s.starts_at)}`))
      return `${names} Next ${next.length === 1 ? 'session' : 'sessions'} ${dates}.`
    }
    case 'masterclass': {
      const rec = (d.recordings || []).find(r => r.product_id === p.id)
      const secs = rec ? (d.recordingLengths || {})[rec.id] : null
      const offer = (d.offers || []).find(o => o.product_id === p.id)?.offer
      const sells = (d.products || []).find(x => x.id === p.offer_product_id)
      const first = secs ? `${duration(secs)}.` : 'No recording yet.'
      if (!offer || offer.offer_on === false) return `${first} The offer is switched off.`
      const at = Number.isFinite(offer.at_seconds) ? clock(offer.at_seconds) : null
      return `${first} The ${sells ? `${sells.name} ` : ''}offer appears${at ? ` at ${at}` : ''}.`
    }
    case 'on_demand': {
      if (p.state === 'draft') {
        const move = MOVES.find(m => m.slug === p.slug)
        return move ? `In build. Moves in from ${move.from}.` : 'In build.'
      }
      const mods = (d.modules || []).filter(m => m.product_id === p.id && m.is_published).length
      const recs = (d.recordings || []).filter(r => r.product_id === p.id && r.is_published).length
      return `${plural(mods, 'module')} and ${plural(recs, 'recording')} switched on.`
    }
    case 'client_space': {
      const spaces = (d.spaces || []).filter(s => s.product_id === p.id).sort((a, b) => Date.parse(a.opened_at) - Date.parse(b.opened_at))
      if (!spaces.length) return 'No clients yet.'
      const name = id => (d.people || []).find(x => x.id === id)?.full_name || 'a client'
      const ids = spaces.map(s => s.id)
      const next = (d.clientSessions || []).filter(s => ids.includes(s.space_id) && s.starts_at && Date.parse(s.starts_at) > nowMs)
        .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0]
      const nextLine = next ? ` Next session ${shortDate(next.starts_at)}, ${time(next.starts_at)}${spaces.length > 1 ? `, with ${name(spaces.find(s => s.id === next.space_id)?.client_person_id)}` : ''}.` : ''
      if (spaces.length === 1) return `1 client: ${name(spaces[0].client_person_id)}, since ${shortDate(spaces[0].opened_at)}.${nextLine}`
      return `${spaces.length} clients: ${joinAnd(spaces.map(s => name(s.client_person_id)))}.${nextLine}`
    }
    default:
      return ''
  }
}

// The free items' line (AdminProducts.dc.html's "what"), from each item's own settings
export function freeLine(p, d, nowMs) {
  const s = p.settings || {}
  if (p.type === 'webinar') {
    const shown = (d.webinars || []).filter(w => w.product_id === p.id && w.shown)
    const up = shown.filter(w => w.starts_at && Date.parse(w.starts_at) + (w.duration_minutes || 60) * 60e3 > nowMs).length
    const replays = shown.filter(w => w.replay_up).length
    const counts = [up ? `${up} coming up` : null, replays ? plural(replays, 'replay') : null].filter(Boolean)
    // changed (stage 6): added by hand, not pulled from Zoom
    return `Added by hand: ${counts.length ? joinAnd(counts) : 'none switched on yet'}. Register opens Zoom's own page, and your Zoom workflow adds them to MailerLite as it does now.`
  }
  if (p.type === 'resource_pack') return "Downloads straight from the hub. No emails follow: it's already theirs."
  if (p.type === 'linked') {
    if (s.join?.route === 'restart_challenge') {
      return "Joins in 1 click: the hub sends their name and email to the challenge's registration, the same one restart.adamslee.co.uk uses. Anyone already in it is skipped, as on the page."
    }
    const host = hostOf(s.link_url)
    if (host === 'scorecard.adamslee.co.uk') return 'Opens scorecard.adamslee.co.uk in a new tab. ScoreApp asks for their first name and email, as it does for everyone.'
    return host ? `Opens ${host} in a new tab. They sign up there, as everyone does.` : 'Its address isn\'t set yet. Add it in its settings.'
  }
  return ''
}

export function hostOf(url) {
  try { return new URL(url).host } catch { return '' }
}

// The manage button: where the product's own content is looked after
export function manageLink(p, d) {
  if (p.access_kind === 'free') {
    // Resources opens on the product named in its address (the Resources screen)
    if (p.type === 'resource_pack') return { label: 'Edit the files', to: `/admin/resources?product=${p.slug}` }
    if (p.type === 'webinar') return { label: 'Webinars', to: '/admin/webinars' }
    return { label: 'Edit', to: `/admin/products/${p.slug}` }
  }
  switch (p.type) {
    case 'mastermind': return { label: 'Groups and dates', to: '/admin/recordings/dates' }
    case 'live_cohort': return { label: 'Cohorts and recordings', to: '/admin/recordings' }
    case 'on_demand': return { label: 'Modules and dates', to: '/admin/recordings' }
    // the offer and the owner's card are on the masterclass's settings, with its recording's line
    case 'masterclass': return { label: 'Recording and offer', to: `/admin/products/${p.slug}#offer` }
    case 'client_space': {
      const spaces = (d.spaces || []).filter(s => s.product_id === p.id)
      if (spaces.length === 1) {
        const person = (d.people || []).find(x => x.id === spaces[0].client_person_id)
        const first = staffFirstName(person)
        if (first) return { label: `${first}'s space`, to: `${p.path || '/mentorship'}?space=${spaces[0].id}` }
      }
      return { label: "Clients' spaces", to: p.path || '/mentorship' }
    }
    default: return { label: 'Recordings and dates', to: '/admin/recordings' }
  }
}

// ---- Prices -------------------------------------------------------------------------------------

// The price ids that open a product, place prices marked, and the balances paid against its places
export function productPrices(productId, map) {
  const rows = (map.priceMap || []).filter(r => r.status === 'mapped')
  const mapped = new Set((map.mapProducts || []).filter(r => r.product_id === productId).map(r => r.stripe_price_id))
  const opens = rows.filter(r => mapped.has(r.stripe_price_id))
  const places = opens.filter(r => r.secures_place).map(r => r.stripe_price_id)
  // the balance a place pays with its pay button (the current one), not every balance ever mapped to it
  const balances = [...new Set((map.balances || []).filter(b => places.includes(b.place_price_id) && b.pay_button !== false).map(b => b.balance_price_id))]
  return { full: opens.filter(r => !r.secures_place).map(r => r.stripe_price_id), places, balances }
}

// 'Stripe: £997+vat, £400+vat to secure a place, £597+vat balance, £1,497+vat'. amounts: stripe-prices'
// { prices: { id: { display, active } } }, or null while it loads, or 'error'.
export function pricesLine(p, map, amounts) {
  if (!isMovedIn(p)) return ''
  const ids = productPrices(p.id, map)
  const any = ids.full.length + ids.places.length > 0
  if (!any) return p.settings?.prices_note || 'Price not set'
  if (amounts === null || amounts === undefined) return "Reading Stripe's prices."
  if (amounts === 'error') return "Stripe's prices didn't load. Reload the page to try again."
  const shown = id => amounts.prices?.[id]
  const live = id => shown(id) && shown(id).active !== false && shown(id).display
  const pick = list => (list.some(live) ? list.filter(live) : list.filter(id => shown(id)?.display))
  const out = []
  const add = s => { if (s && !out.includes(s)) out.push(s) }
  for (const id of pick(ids.full)) add(shown(id).display + intervalWords(shown(id).interval))
  for (const id of pick(ids.places)) add(`${shown(id).display} to secure a place`)
  for (const id of pick(ids.balances)) add(`${shown(id).display} balance`)
  return out.length ? `Stripe: ${out.join(', ')}` : 'Its prices are in the Price map.'
}
export const intervalWords = i => (i ? ` a ${i}` : '')

// ---- Checklist ----------------------------------------------------------------------------------

export function checklistFor(productId, items) {
  return (items || []).filter(i => i.product_id === productId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.id).localeCompare(String(b.id)))
}

// Move 1 step up: the sort_order changes that make the new order (each item 1, 2, 3 ... in turn)
export function moveUpChanges(list, index) {
  if (index <= 0 || index >= list.length) return []
  const order = list.slice()
  ;[order[index - 1], order[index]] = [order[index], order[index - 1]]
  return order.map((item, i) => ({ id: item.id, sort_order: i + 1 })).filter((c, i) => order[i].sort_order !== c.sort_order)
}

export const nextSortOrder = list => (list.length ? Math.max(...list.map(i => i.sort_order ?? 0)) + 1 : 1)

// ---- Duplicate ----------------------------------------------------------------------------------

// What Duplicate copies: the catalogue row as a draft, its checklist, its page layouts, its weeks or
// modules and its Resources tabs (data.js duplicateProduct). Never the masterclass offer or what it
// sells, prices, product_extras, cohorts, groups, sessions, recordings, client spaces, the certificate
// prefix (numbers stay the product's own) or the cover (it sits in the original's own folder). Its
// settings come without the schedule, the Restart Challenge's 1-click join (a copy is a plain link
// with no address yet), the prices note (the original's prices) and the old wish for a login page. A
// mastermind's guarantee comes switched off, its words and limit kept, so members see none until it's
// switched on in the copy's settings (the product-types follow-ups, 13 Oct). The terms link (terms_url)
// comes as it is, and can be changed in the copy's settings.
export const COPY_COLUMNS = [
  'type', 'access_kind', 'look', 'own_look', 'own_look_contrast_ok', 'tagline', 'questions_email', 'live_sessions_audience',
  'release_basis', 'join_opens_minutes', 'default_session_minutes', 'watched_share', 'min_access_months', 'min_access_from',
  'term_months', 'terms_line', 'certificate_on', 'certificate_rule', 'certificate_min_sessions',
]
export function duplicateRow(src, all, actorId) {
  const taken = all.map(p => p.slug)
  const slug = uniqueSlug(`${src.slug}-copy`, taken)
  const row = Object.fromEntries(COPY_COLUMNS.filter(k => src[k] !== undefined).map(k => [k, src[k]]))
  const settings = { ...(src.settings || {}) }
  for (const k of ['schedule', 'join', 'prices_note', 'door_wanted']) delete settings[k]
  if (settings.guarantee && typeof settings.guarantee === 'object') settings.guarantee = { ...settings.guarantee, shown: false }
  return {
    ...row,
    slug,
    path: src.path ? `/${slug}` : null,
    name: `${src.name} (copy)`.slice(0, 120),
    state: 'draft',
    settings,
    sort_order: nextSortOrder(all.filter(p => p.access_kind === src.access_kind)),
    duplicated_from_id: src.id,
    created_by: actorId || null,
    updated_by: actorId || null,
  }
}

// ---- Add a product: the schedule, access runs and the row ----------------------------------------

// The sessions a new live cohort's schedule makes: count weeks from the first <day> on or after the
// start date, at the time picked (UK). Each is { title, number, starts_at (ISO) }.
export function scheduleSessions({ startOn, day, at, count }, ukToIso) {
  if (!startOn || day === null || day === undefined || !at || !count) return []
  const [y, m, d] = startOn.split('-').map(Number)
  const first = new Date(Date.UTC(y, m - 1, d))
  const shift = (Number(day) - first.getUTCDay() + 7) % 7
  const out = []
  for (let i = 0; i < count; i++) {
    const t = new Date(Date.UTC(y, m - 1, d + shift + 7 * i))
    const iso = t.toISOString().slice(0, 10)
    out.push({ title: `Week ${i + 1}`, number: i + 1, starts_on: iso, starts_at: ukToIso(iso, at) })
  }
  return out
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const dayName = d => DAY_NAMES[d] || ''
export const scheduleLabel = (day, at) => `${DAY_NAMES[day]}s at ${time({ hh: Number(at.split(':')[0]), mm: Number(at.split(':')[1]), day: 0 })}`
export const firstSessionWords = iso => `${longDate(iso)}`

// "Access runs" as columns (data-model 4.2): the course's run with a 12-month minimum, a
// subscription, for good, or a mastermind's 12-month membership, paid monthly (the Accelerator's own
// values in docs/live-schema/config.sql). "Until a date I set" has no home in the product (data-model
// fit 20), so it isn't offered; a grant's end date is set on the grant.
export function accessColumns(run, from) {
  if (run === 'membership') return { term_months: 12, min_access_months: null, min_access_from: null, terms_line: '12-month membership.' }
  if (run === 'term') {
    return { min_access_months: 12, min_access_from: from || 'purchase', term_months: null, terms_line: 'For as long as the course runs, and at least 12 months.' }
  }
  if (run === 'subscription') return { min_access_months: null, min_access_from: null, term_months: null, terms_line: 'While your subscription is paid.' }
  if (run === 'for_good') return { min_access_months: null, min_access_from: null, term_months: null, terms_line: 'Yours for good.' }
  return {}
}

// The ways a paid type's access can run, as Add a product offers them: a mastermind is always a
// membership of a set length, as the Accelerator is, since its pages count the months of the term
// (Month 3 of 12; the length is set in its settings); a private client space has none (nobody buys it
// in the hub: Adam opens each client's space by hand); every other paid type the 3 it always had
export function runsFor(type) {
  if (type === 'mastermind') return ['membership']
  if (type === 'client_space') return []
  return ['term', 'subscription', 'for_good']
}

// Whether a product is paid monthly: a mastermind's membership or a subscription (its access run), or
// a ticked Stripe price that repeats. Its purchases stay in shadow when it's made (data.js createProduct)
// until the Accelerator's old payment flows are read (the plan's step 8.4). prices: Add a product's
// ticked prices, each with Stripe's interval
export function paidMonthly(row, prices = []) {
  const run = row?.settings?.access_runs
  return row?.type === 'mastermind' || run === 'membership' || run === 'subscription' || (prices || []).some(p => Boolean(p?.interval))
}

// The pieces for a type, and the defaults Add a product starts with (all on, as the board has them)
export const piecesFor = type => PIECES[type] || []

// A new live cohort's weeks and a new on-demand course's modules, made with it (hub.modules). The
// square brackets mark a title still to come (lib/format.js isPlaceholder), so members see "Week 3"
// until the week has a title of its own; its first recording gives it 1 (content/data.js saveRecording).
export const weeksFor = count => Array.from({ length: Number(count) || 0 }, (_, i) => ({
  key: `week-${i + 1}`, number: i + 1, label: `Week ${i + 1}`, title: `[Week ${i + 1} title]`, sort_order: i + 1,
}))
export const moduleFor = n => ({ key: `m${n}`, number: n, label: `Module ${n}`, title: `[Module ${n} title]`, sort_order: n })
export const modulesFor = count => Array.from({ length: Number(count) || 0 }, (_, i) => moduleFor(i + 1))

// The Resources tabs a new product starts with, by the pieces ticked (hub.resource_sections): a live
// cohort's worksheets, prompts and its pack, each only where its piece is ticked, else 1 called
// Resources; an on-demand course's and a paid resource pack's Downloads. Any other type starts with
// none: the masterclass has no Resources until something is added (decision 32).
export function startingSections(type, pieces = [], access = 'paid') {
  const has = k => (pieces || []).includes(k)
  if (type === 'live_cohort') {
    const tabs = [has('worksheets') && 'Worksheets', has('prompts') && 'Prompts', has('pack') && 'Scripts and templates'].filter(Boolean)
    return tabs.length ? tabs : ['Resources']
  }
  if (type === 'on_demand') return has('downloads') ? ['Downloads'] : []
  if (type === 'resource_pack' && access === 'paid') return ['Downloads']
  return []
}

// The catalogue row Add a product inserts, as a draft
export function newProductRow(f, all, actorId) {
  const access = accessFor(f.type) === 'either' ? f.access : accessFor(f.type)
  const base = slugFor(f.name)
  const slug = uniqueSlug(base || 'product', all.map(p => p.slug))
  const free = access === 'free'
  const client = f.type === 'client_space'
  const looks = looksFor(f.type)
  const look = looks.includes(f.look) ? f.look : looks[0]
  const run = runsFor(f.type).includes(f.run) ? f.run : runsFor(f.type)[0]
  const settings = {}
  if (f.pieces?.length) settings.pieces = f.pieces
  if (free && f.type === 'linked') settings.link_url = String(f.address || '').trim()
  if (!free && !client && run) settings.access_runs = run
  if (f.type === 'live_cohort' && f.startOn) settings.schedule = { first_date: f.startOn, day: f.day, time: f.at, count: f.count }
  if (f.type === 'live_cohort') settings.cohort_naming = { pattern: 'Cohort {n}' }
  if (f.type === 'mastermind') settings.cohort_naming = { pattern: 'Group {n}' }
  const row = {
    slug,
    path: free ? null : `/${slug}`,
    name: f.name.trim(),
    tagline: f.line.trim() || null,
    type: f.type,
    access_kind: access,
    state: 'draft',
    look,
    questions_email: (f.help || '').trim() || 'course@adamslee.co.uk',
    settings,
    sort_order: nextSortOrder(all.filter(p => p.access_kind === access)),
    created_by: actorId || null,
    updated_by: actorId || null,
  }
  // released to each intake after its session, as The Funding Engine and Profit On Your Terms run
  if (f.type === 'live_cohort') row.release_basis = 'own_intake'
  if (look === 'own') {
    const own = { bg: normalHex(f.ownBg), accent: normalHex(f.ownAccent), text: normalHex(f.ownText), heading_font: f.font }
    row.own_look = own
    row.own_look_contrast_ok = contrastCheck(own).pass
  }
  // a private client space has no terms of its own: Account keeps the 1-2-1's own line
  if (!free && !client) Object.assign(row, accessColumns(run, f.from))
  return row
}

// Add a product's done screen: what to do next, by type, then anything that didn't save
// (data.js createProduct's answer: the cohort, its sessions, the weeks or modules made, whether its Stripe
// purchases went live, and the problems)
export function doneLines({ made, owner = true, cohort, sessions, weeks, modules, live, held = false, problems = [] }) {
  const lines = []
  const paid = made.access_kind === 'paid'
  if (made.type === 'client_space') lines.push(ADD.doneClientSpace)
  else if (paid) {
    lines.push(ADD.doneLine)
    // an owner's purchases went live (data.js createProduct), or stay in shadow for a product paid
    // monthly; an admin's wait for the owner, and a failed switch says so at the end
    if (live) lines.push(ADD.doneSells)
    else if (held) lines.push(ADD.doneMonthly)
    else if (!problems.includes('billing')) lines.push(ADD.doneShadow)
  }
  else lines.push(ADD.doneFree)
  // with no weeks and no sessions saved, the weeks' own problem line says what's left
  if (made.type === 'live_cohort' && cohort) {
    if (cohort.starts_on) lines.push(ADD.doneCohort(cohort.name, sessions))
    else if (weeks || sessions) lines.push(ADD.doneWeeks(weeks || sessions))
  }
  if (made.type === 'mastermind' && !problems.includes('group')) lines.push(ADD.doneMastermind)
  if (made.type === 'on_demand' && modules) lines.push(ADD.doneModules(modules))
  // only an owner sets what a masterclass sells and its offer (OfferEditor)
  if (made.type === 'masterclass') lines.push(owner ? ADD.doneMasterclass : ADD.doneMasterclassAdmin)
  if (made.type === 'resource_pack') lines.push(ADD.donePack)
  const failed = {
    weeks: made.type === 'on_demand' ? ADD.doneModulesFailed : ADD.doneWeeksFailed, cohort: ADD.doneCohortFailed,
    group: ADD.doneGroupFailed, tabs: ADD.doneTabsFailed, price: ADD.donePriceFailed, billing: ADD.doneBillingFailed, cover: ADD.doneCoverFailed,
  }
  for (const [key, line] of Object.entries(failed)) if (problems.includes(key)) lines.push(line)
  return lines
}

// ---- Page layout --------------------------------------------------------------------------------

// Each section's admin name and what it is (AdminLayout.dc.html; the on-demand home's aren't drawn
// on any board, so its words are new), and the defaults in code, so fixed comes from here and never
// from what the browser sends
const SECTION_WORDS = {
  news: ['Announcements', 'Shows only while you have 1 posted for this product'],
  next: ['Next live session', 'The date, Join and Add to calendar'],
  resume: ['Carry on watching', 'Where they stopped in the last recording'],
  weeks: ['Your 6 weeks', "Every week, its date and what they've done"],
  todo: ['Still to tick', "Homework they haven't ticked yet"],
  help: ['Stuck on anything?', 'How to reach Adam between sessions'],
  res: ['Resources', 'Worksheets and prompts, in tabs'],
  wnews: ['Announcements', 'Shows only while you have 1 posted for this product'],
  video: ['Video and chapters', 'The recording, with the chapter list beside it'],
  summary: ['Session summary', 'A paragraph on what the week covered'],
  five: ['If you only take 5 things', 'The 5 points to remember'],
  learn: ['Key learnings and timestamps', 'Each point, with the minute it comes up'],
  hw: ['Your homework', 'Tick boxes, saved as they go'],
  sheet: ['Worksheet', 'The download for the week'],
  prompts: ['Prompts', 'Each prompt with a Copy button'],
}
const ON_DEMAND_WORDS = {
  news: SECTION_WORDS.news,
  where: ["Where they're up to", 'The 5 Ms, the module they\'re on and their homework line'],
  next: ['Up next', 'Start here until it\'s done, then the recording to watch next'],
  hw: ['Homework', "The homework of the module they're on"],
}

// The pages a product's layout covers, or null where Page layout isn't offered: only where the
// product's own pages read a layout (src/product/screens.js): The Funding Engine, and a live cohort in
// its look, which takes its pages (their home and week pages); Profit On Your Terms (its home, and its
// week pages, the shared recording page's); a mastermind like The Property Accelerator (its home); and
// an on-demand course like Property Cashflow For Life (its home and its recording pages). The
// masterclass and the 1-2-1 have no layout their pages read, and a live cohort in any other look is on
// a holding page until its own pages come.
const HOME = { key: 'home', label: 'Home page', list: 'Sections on the home page' }
const WEEK = { key: 'week', label: 'Week page', list: 'Sections on every week page' }
export function layoutPages(p) {
  if (!p || !isMovedIn(p) || p.access_kind !== 'paid') return null
  const prefix = prefixFor(p)
  if (prefix === 'FundingEngine' && p.type === 'live_cohort') return [HOME, WEEK]
  if (prefix === 'Poyt' && p.type === 'live_cohort') return [HOME, WEEK]
  if (prefix === 'Mastermind' && p.type === 'mastermind') return [HOME]
  if (prefix === 'OnDemand' && p.type === 'on_demand') return [HOME, { key: 'week', label: 'Recording page', list: 'Sections on every recording page' }]
  return null
}
export const hasLayout = p => Boolean(layoutPages(p))

// Which defaults and words: a product (Profit On Your Terms has its own, by slug, as its pages do:
// src/product/layout.js layoutKind), or a type
const kindOf = x => (typeof x === 'string' ? x : layoutKind(x))

export function defaultSections(product, page) {
  const kind = kindOf(product)
  if (page === 'week') return WEEK_PAGE
  if (kind === 'on_demand') return ON_DEMAND_HOME
  if (kind === 'mastermind') return MASTERMIND_HOME
  if (kind === 'poyt') return POYT_HOME
  return LIVE_COHORT_HOME
}
export function sectionWords(product, key) {
  const kind = kindOf(product)
  const w = (kind === 'on_demand' && ON_DEMAND_WORDS[key]) || HOME_SECTION_WORDS[kind]?.[key] || SECTION_WORDS[key] || [key, '']
  return { name: w[0], what: w[1] }
}

// The list to edit: what's saved (a draft, else the published version), checked against the default
// in code: unknown keys dropped, missing ones put back after their neighbour, fixed from the code.
// Phase 2's fields (cols, width, surface, settings) are kept as they were.
export function editableSections(type, page, saved) {
  const defaults = defaultSections(type, page)
  const known = new Map(defaults.map(d => [d.key, d]))
  const out = []
  for (const s of Array.isArray(saved) ? saved : []) {
    if (!s || !known.has(s.key) || out.some(o => o.key === s.key)) continue
    const fixed = known.get(s.key).fixed
    out.push({ ...s, key: s.key, fixed, shown: fixed ? true : s.shown !== false })
  }
  defaults.forEach((d, i) => {
    if (out.some(o => o.key === d.key)) return
    let at = 0
    for (let j = i - 1; j >= 0; j--) {
      const k = out.findIndex(o => o.key === defaults[j].key)
      if (k >= 0) { at = k + 1; break }
    }
    out.splice(at, 0, { key: d.key, shown: true, fixed: d.fixed })
  })
  return out
}

// The sections as they're saved: every key in order, shown, fixed from the code, phase 2's fields kept
export function sectionsToSave(type, page, list) {
  const defaults = defaultSections(type, page)
  const known = new Map(defaults.map(d => [d.key, d]))
  return list.filter(s => known.has(s.key)).map(s => {
    const fixed = known.get(s.key).fixed
    return {
      key: s.key, shown: fixed ? true : Boolean(s.shown), fixed,
      cols: s.cols ?? 1, width: s.width ?? 'contained', surface: s.surface ?? 'page', built_in: true, settings: s.settings ?? {},
    }
  })
}

export const sameOrder = (a, b) => JSON.stringify((a || []).map(s => [s.key, Boolean(s.shown)])) === JSON.stringify((b || []).map(s => [s.key, Boolean(s.shown)]))

// ---- Small helpers ------------------------------------------------------------------------------

export function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`
}
export function joinAnd(list) {
  if (list.length <= 1) return list.join('')
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}
// a first name for staff screens: "there" or a bare title gives the full name, and a full name starting with a title
// isn't cut to the title (lib/names.js)
export const firstNameOf = person => staffFirstName(person)

// 1:35:00 as seconds, or null
export function parseStamp(s) {
  const m = String(s || '').trim().match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = Number(m[1] || 0), min = Number(m[2]), sec = Number(m[3])
  if (min > 59 || sec > 59) return null
  return h * 3600 + min * 60 + sec
}

export const isEmail = s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim())
export const isHttps = s => /^https:\/\/\S+$/.test(String(s || '').trim())
